import React, { useState, useCallback, useEffect, useRef } from 'react';
import { AppState, Status } from './types';
import { extractTextFromFile, type ExtractionProgress } from './services/fileParser';
import { validateFile } from './services/fileValidation';
import { redactTextWithGemini } from './services/geminiService';
import { verifyPayment, initiateCheckout } from './services/stripeService';
import { useSubscription } from './contexts/SubscriptionContext';
import { useAuth } from './contexts/AuthContext';
import { captureProcessingError, Sentry } from './lib/sentry';
import InputSelector, { type PrepareOptions } from './components/InputSelector';
import ProcessingView from './components/ProcessingView';
import ResultDisplay from './components/ResultDisplay';
import BatchProcessingView, { FileProcessingStatus } from './components/BatchProcessingView';
import BatchResultDisplay from './components/BatchResultDisplay';
import PricingTiers from './components/PricingTiers';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import UpgradeModal from './components/UpgradeModal';
import LimitReachedModal from './components/LimitReachedModal';
import AuthModal from './components/AuthModal';
import CreateAccountModal from './components/CreateAccountModal';
import ManageSubscriptionModal from './components/ManageSubscriptionModal';
import SettingsModal from './components/SettingsModal';
import { createSession, registerDoc, generateFilename, RedactSession } from './utils/registryUtils';
import { SmartRedactMode } from './components/SmartRedactMode';
import {
  PAGE_INTEGRITY_USER_MESSAGE,
  validatePageIntegrity,
} from './utils/pageIntegrity';
import { PositioningSections } from './components/PositioningSections';
import HomeLanding from './components/HomeLanding';
import {
  accountSegmentFromTier,
  trackPrepareEvent,
} from './lib/productAnalytics';
import { isEducatorPilotTier } from './types/tiers';
import { hasAuthCallbackParams } from './lib/authCallback';

function getExtractionStatusMessage(progress: ExtractionProgress): string {
  switch (progress.phase) {
    case 'detecting':
      return 'Analyzing document...';
    case 'extracting':
      return 'Extracting text from document...';
    case 'ocr':
      return `OCR-ing page ${progress.currentPage} of ${progress.totalPages}...`;
  }
}

function AppContent() {
  const redactSession = useRef<RedactSession>(createSession());
  const [appState, setAppState] = useState<AppState>({
    status: Status.IDLE,
    file: null,
    redactedText: null,
    error: null,
    statusMessage: '',
    sourceName: '',
    inputMode: 'file',
  });

  // Batch processing state
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [batchFiles, setBatchFiles] = useState<FileProcessingStatus[]>([]);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [batchComplete, setBatchComplete] = useState(false);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [proCheckoutLoading, setProCheckoutLoading] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [showManageSubscriptionModal, setShowManageSubscriptionModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [appMode, setAppMode] = useState<'standard' | 'smart'>('standard');

  const openSettings = useCallback(() => setShowSettingsModal(true), []);

  const [limitModalType, setLimitModalType] = useState<'daily' | 'file_size' | 'pages'>('daily');
  const [limitModalDetails, setLimitModalDetails] = useState<string | undefined>();

  const { user, loading: authLoading, authNotice, clearAuthNotice, needsPasswordReset } = useAuth();
  const {
    subscription,
    limits,
    canRedact,
    incrementUsage,
    applyOptimisticSubscription,
    checkSubscriptionStatus,
    syncSubscriptionFromDB,
  } = useSubscription();

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('type=recovery')) {
      setShowSignInModal(true);
    }
  }, []);

  useEffect(() => {
    if (needsPasswordReset) {
      setShowSignInModal(true);
    }
  }, [needsPasswordReset]);

  const isPremium = subscription.isActive && subscription.tier !== 'free';
  const isPilot = isEducatorPilotTier(subscription.tier);
  const accountSegment = accountSegmentFromTier(subscription.tier, subscription.isActive);
  const prepareCategoryRef = useRef<string | undefined>(undefined);
  const prepareStartedAtRef = useRef<number>(0);
  const isTrialActive =
    !!user &&
    subscription.isActive &&
    (subscription.status === 'trialing' ||
      (subscription.tier === 'monthly_pro' &&
        subscription.status !== 'active' &&
        !!subscription.expiresAt &&
        subscription.expiresAt > Date.now() &&
        subscription.expiresAt - Date.now() <= 7 * 24 * 60 * 60 * 1000));
  const trialEndsAt = isTrialActive && subscription.expiresAt ? new Date(subscription.expiresAt) : null;
  const trialEndsFormatted = trialEndsAt
    ? trialEndsAt.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
    : '';
  const trialDaysRemaining =
    isTrialActive && subscription.expiresAt
      ? Math.max(1, Math.ceil((subscription.expiresAt - Date.now()) / (24 * 60 * 60 * 1000)))
      : 0;
  const shouldHidePricingSection =
    !!user &&
    subscription.isActive &&
    ['monthly_pro', 'annual_pro', 'campus_monthly', 'campus_annual', 'district', 'educator_pilot'].includes(subscription.tier);
  
  // Determine max files based on tier
  const getMaxFiles = () => {
    if (!isPremium) return 1;
    if (subscription.tier === 'day_pass') return 1;
    return 5; // Pro, Campus, District get batch upload
  };
  
  const maxFiles = getMaxFiles();
  const batchEnabled = maxFiles > 1;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const plan = urlParams.get('plan');

    if (sessionId && plan) {
      verifyPayment(sessionId)
        .then(async (result) => {
          // Optimistic UI only — server already wrote trusted entitlement
          applyOptimisticSubscription(result.tier, result.expiresAt);
          await syncSubscriptionFromDB();
          window.history.replaceState({}, '', '/dashboard');
        })
        .catch((error) => {
          console.error('Payment verification failed:', error);
          // Drop optimistic state; re-read trusted DB row
          void syncSubscriptionFromDB();
        });
    }

    const interval = setInterval(checkSubscriptionStatus, 60000);
    return () => clearInterval(interval);
  }, [applyOptimisticSubscription, checkSubscriptionStatus, syncSubscriptionFromDB]);

  useEffect(() => {
    if (user) {
      syncSubscriptionFromDB();
    }
  }, [user, syncSubscriptionFromDB]);

  useEffect(() => {
    if (!user) return;
    // Never strip email-confirm / recovery tokens before AuthContext finishes.
    if (hasAuthCallbackParams()) return;
    if (window.location.pathname !== '/dashboard') {
      window.history.replaceState({}, '', '/dashboard');
    }
  }, [user]);

  // Process batch files sequentially
  useEffect(() => {
    if (!isBatchMode || batchComplete || batchFiles.length === 0) return;
    
    const currentFile = batchFiles[currentBatchIndex];
    if (!currentFile || currentFile.status !== 'pending') return;

    const processCurrentFile = async () => {
      // Update status to processing
      setBatchFiles(prev => prev.map((f, i) => 
        i === currentBatchIndex ? { ...f, status: 'processing' as const, progress: 'Analyzing document...' } : f
      ));

      try {
        const { text, ocrPageFailures, pageCount } = await extractTextFromFile(currentFile.file, {
          knownAnalysis: currentFile.pdfAnalysis,
          onProgress: (progress) => {
            setBatchFiles(prev => prev.map((f, i) =>
              i === currentBatchIndex
                ? { ...f, progress: getExtractionStatusMessage(progress) }
                : f
            ));
          },
        });

        // Redact
        setBatchFiles(prev => prev.map((f, i) => 
          i === currentBatchIndex ? { ...f, progress: 'Preparing document for AI...' } : f
        ));
        const redactResult = await redactTextWithGemini(text);
        const redactedContent = redactResult.redactedText;
        const expectedPages =
          pageCount && pageCount > 0 ? pageCount : redactResult.originalPageCount || 0;
        if (expectedPages > 0) {
          const integrity = validatePageIntegrity(redactedContent, expectedPages);
          if (!integrity.isComplete) {
            Sentry.captureMessage('client_page_integrity_mismatch', {
              level: 'error',
              tags: { error_stage: 'output', processing_mode: 'batch' },
              extra: {
                original_page_count: integrity.originalPageCount,
                processed_page_count: integrity.processedPageCount,
                missing_page_count: integrity.missingPageNumbers.length,
                missing_pages: integrity.missingPageNumbers.slice(0, 50),
              },
            });
            trackPrepareEvent('document_prepare_failed', {
              accountSegment,
              pageCount: expectedPages,
              documentCategory: prepareCategoryRef.current,
              success: false,
              failureReason: 'integrity',
            });
            throw new Error(PAGE_INTEGRITY_USER_MESSAGE);
          }
        }
        trackPrepareEvent('document_prepare_succeeded', {
          accountSegment,
          pageCount: expectedPages || pageCount,
          documentCategory: prepareCategoryRef.current,
          success: true,
          durationMs: Date.now() - prepareStartedAtRef.current,
          fileExtension: currentFile.file.name.split('.').pop(),
        });
        const student = registerDoc(
          redactSession.current,
          redactedContent,
          currentFile.file.name,
          redactResult.studentName,
          redactResult.grade,
          redactResult.docType
        );
        const autoName = generateFilename(student, currentFile.file.name);

        // Success
        setBatchFiles(prev => prev.map((f, i) => 
          i === currentBatchIndex
            ? {
                ...f,
                status: 'success' as const,
                result: redactedContent,
                autoName,
                studentToken: student.token,
                progress: undefined,
                ocrPageFailures,
                pageCount,
              }
            : f
        ));

        if (!isPremium) {
          incrementUsage();
        }
      } catch (err) {
        console.error('Batch processing error:', err);
        const errorObj = err instanceof Error ? err : new Error(String(err));
        const errorMessage = errorObj.message || 'An unknown error occurred.';
        
        // Capture error in Sentry with file metadata (not content)
        const fileExt = currentFile.file.name.split('.').pop()?.toLowerCase() || 'unknown';
        const sentryEventId = captureProcessingError(errorObj, {
          fileType: fileExt,
          fileSize: currentFile.file.size,
          tier: subscription.tier,
          errorStage: 'gemini_call',
          processingMode: 'batch',
        });
        
        setBatchFiles(prev => prev.map((f, i) => 
          i === currentBatchIndex ? { 
            ...f, 
            status: 'error' as const, 
            error: errorMessage, 
            progress: undefined,
            sentryEventId,
          } : f
        ));
      }

      // Move to next file or complete (with delay to avoid rate limiting)
      if (currentBatchIndex < batchFiles.length - 1) {
        // Add 2 second delay before processing next file
        await new Promise(resolve => setTimeout(resolve, 2000));
        setCurrentBatchIndex(prev => prev + 1);
      } else {
        setBatchComplete(true);
      }
    };

    processCurrentFile();
  }, [isBatchMode, currentBatchIndex, batchFiles, batchComplete, isPremium, incrementUsage]);

  const processRedaction = useCallback(async (
    text: string,
    sourceName: string,
    inputMode: 'file' | 'text',
    ocrPageFailures?: number[],
    documentPageCount?: number
  ) => {
    setAppState(prev => ({ ...prev, statusMessage: 'Preparing document for AI use...' }));
    
    try {
      const redactResult = await redactTextWithGemini(text);
      const redactedContent = redactResult.redactedText;

      const expectedPages =
        documentPageCount && documentPageCount > 0
          ? documentPageCount
          : redactResult.originalPageCount || 0;

      if (expectedPages > 0) {
        const integrity = validatePageIntegrity(redactedContent, expectedPages);
        if (!integrity.isComplete) {
          Sentry.captureMessage('client_page_integrity_mismatch', {
            level: 'error',
            tags: { error_stage: 'output' },
            extra: {
              original_page_count: integrity.originalPageCount,
              processed_page_count: integrity.processedPageCount,
              missing_page_count: integrity.missingPageNumbers.length,
              missing_pages: integrity.missingPageNumbers.slice(0, 50),
              source_name_length: sourceName.length,
            },
          });
          trackPrepareEvent('document_prepare_failed', {
            accountSegment,
            pageCount: expectedPages,
            documentCategory: prepareCategoryRef.current,
            success: false,
            failureReason: 'integrity',
          });
          setAppState(prev => ({
            ...prev,
            status: Status.ERROR,
            error: PAGE_INTEGRITY_USER_MESSAGE,
            statusMessage: '',
            processingPage: undefined,
            processingTotalPages: undefined,
          }));
          return;
        }
      }

      if (!isPremium) {
        incrementUsage();
      }

      trackPrepareEvent('document_prepare_succeeded', {
        accountSegment,
        pageCount: expectedPages || documentPageCount,
        documentCategory: prepareCategoryRef.current,
        success: true,
        durationMs: Date.now() - prepareStartedAtRef.current,
        fileExtension: sourceName.split('.').pop(),
      });

      setAppState(prev => ({
        ...prev,
        status: Status.SUCCESS,
        redactedText: redactedContent,
        statusMessage: 'Document prepared successfully!',
        sourceName,
        inputMode,
        processingPage: undefined,
        processingTotalPages: undefined,
        documentPageCount: expectedPages || documentPageCount,
        ocrPageFailures,
      }));
    } catch (err) {
      console.error(err);
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      trackPrepareEvent('document_prepare_failed', {
        accountSegment,
        documentCategory: prepareCategoryRef.current,
        success: false,
        failureReason: 'processing',
        durationMs: Date.now() - prepareStartedAtRef.current,
      });
      setAppState(prev => ({
        ...prev,
        status: Status.ERROR,
        error: `Failed to prepare the document. ${errorMessage}`,
        statusMessage: '',
      }));
    }
  }, [isPremium, incrementUsage, accountSegment]);

  const handleFileSelect = useCallback(async (selectedFile: File, options?: PrepareOptions) => {
    if (!selectedFile) return;

    if (!user) {
      setShowSignInModal(true);
      return;
    }

    if (!isPremium && !canRedact()) {
      setLimitModalType('daily');
      setLimitModalDetails(undefined);
      setShowLimitModal(true);
      return;
    }

    prepareCategoryRef.current = options?.documentCategory;
    prepareStartedAtRef.current = Date.now();
    trackPrepareEvent('document_prepare_started', {
      accountSegment,
      documentCategory: options?.documentCategory,
      fileExtension: selectedFile.name.split('.').pop(),
    });

    const validation = await validateFile(selectedFile, limits);
    if (!validation.isValid) {
      if (validation.errorType === 'file_size') {
        setLimitModalType('file_size');
      } else if (validation.errorType === 'page_limit') {
        setLimitModalType('pages');
      } else if (validation.errorType === 'scanned_page_limit') {
        setAppState({
          status: Status.ERROR,
          file: null,
          redactedText: null,
          error: validation.error || 'Invalid file',
          statusMessage: '',
          sourceName: selectedFile.name,
          inputMode: 'file',
        });
        return;
      } else {
        setAppState({
          status: Status.ERROR,
          file: null,
          redactedText: null,
          error: validation.error || 'Invalid file',
          statusMessage: '',
          sourceName: '',
          inputMode: 'file',
        });
        return;
      }
      setLimitModalDetails(validation.error);
      setShowLimitModal(true);
      return;
    }

    setAppState({
      status: Status.PROCESSING,
      file: selectedFile,
      redactedText: null,
      error: null,
      statusMessage: 'Preparing your document...',
      sourceName: selectedFile.name,
      inputMode: 'file',
    });

    try {
      const { text, ocrPageFailures, pageCount } = await extractTextFromFile(selectedFile, {
        knownAnalysis: validation.pdfAnalysis,
        onProgress: (progress) => {
          setAppState(prev => ({
            ...prev,
            statusMessage: getExtractionStatusMessage(progress),
            processingPage: progress.phase === 'ocr' ? progress.currentPage : undefined,
            processingTotalPages: progress.phase === 'ocr' ? progress.totalPages : undefined,
          }));
        },
      });
      
      await processRedaction(text, selectedFile.name, 'file', ocrPageFailures, pageCount);
    } catch (err) {
      console.error(err);
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setAppState(prev => ({
        ...prev,
        status: Status.ERROR,
        error: `Failed to process the document. ${errorMessage}`,
        statusMessage: '',
      }));
    }
  }, [user, isPremium, canRedact, limits, processRedaction, accountSegment]);

  // Handle batch file selection
  const handleFilesSelect = useCallback(async (files: File[], options?: PrepareOptions) => {
    if (!user) {
      setShowSignInModal(true);
      return;
    }

    if (!isPremium && !canRedact()) {
      setLimitModalType('daily');
      setLimitModalDetails(undefined);
      setShowLimitModal(true);
      return;
    }

    prepareCategoryRef.current = options?.documentCategory;
    prepareStartedAtRef.current = Date.now();
    trackPrepareEvent('document_prepare_started', {
      accountSegment,
      documentCategory: options?.documentCategory,
      fileExtension: files[0]?.name.split('.').pop(),
    });

    // Validate all files first
    const validatedFiles: FileProcessingStatus[] = [];
    for (const file of files) {
      const validation = await validateFile(file, limits);
      if (!validation.isValid) {
        validatedFiles.push({
          file,
          status: 'error',
          error: validation.error || 'Invalid file',
        });
      } else {
        validatedFiles.push({
          file,
          status: 'pending',
          pdfAnalysis: validation.pdfAnalysis,
        });
      }
    }

    // Start batch processing
    setIsBatchMode(true);
    setBatchFiles(validatedFiles);
    setCurrentBatchIndex(0);
    setBatchComplete(false);
  }, [user, isPremium, canRedact, limits, accountSegment]);

  const handleTextSubmit = useCallback(async (text: string, options?: PrepareOptions) => {
    if (!user) {
      setShowSignInModal(true);
      return;
    }

    if (!isPremium && !canRedact()) {
      setLimitModalType('daily');
      setLimitModalDetails(undefined);
      setShowLimitModal(true);
      return;
    }

    prepareCategoryRef.current = options?.documentCategory;
    prepareStartedAtRef.current = Date.now();
    trackPrepareEvent('document_prepare_started', {
      accountSegment,
      documentCategory: options?.documentCategory,
      fileExtension: 'txt',
    });

    setAppState({
      status: Status.PROCESSING,
      file: null,
      redactedText: null,
      error: null,
      statusMessage: 'Preparing your text...',
      sourceName: 'Pasted Text',
      inputMode: 'text',
    });

    await processRedaction(text, 'Pasted Text', 'text');
  }, [user, isPremium, canRedact, processRedaction, accountSegment]);

  const handleReset = () => {
    redactSession.current = createSession();
    setAppState({
      status: Status.IDLE,
      file: null,
      redactedText: null,
      error: null,
      statusMessage: '',
      sourceName: '',
      inputMode: 'file',
      processingPage: undefined,
      processingTotalPages: undefined,
      documentPageCount: undefined,
      ocrPageFailures: undefined,
    });
    // Reset batch state
    setIsBatchMode(false);
    setBatchFiles([]);
    setCurrentBatchIndex(0);
    setBatchComplete(false);
  };

  const handleOpenUpgradeModal = () => {
    setShowLimitModal(false);
    setShowUpgradeModal(true);
  };

  const handleTryFreeThenPay = useCallback(async () => {
    if (!user) {
      setShowCreateAccountModal(true);
      return;
    }
    setProCheckoutLoading(true);
    try {
      await initiateCheckout('monthly_pro');
    } catch (e) {
      console.error(e);
      alert('Failed to start checkout. Please try again.');
      setProCheckoutLoading(false);
    }
  }, [user]);

  const openLoginModal = () => {
    setShowCreateAccountModal(false);
    setShowSignInModal(true);
  };

  const openSignupModal = () => {
    setShowSignInModal(false);
    setShowCreateAccountModal(true);
  };

  const renderContent = () => {
    if (authLoading) {
      return (
        <div className="w-full text-center p-8 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-center gap-3">
            <svg className="animate-spin h-6 w-6 text-emerald-600" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span className="text-slate-600 dark:text-slate-300">Loading...</span>
          </div>
        </div>
      );
    }

    if (!user) {
      return (
        <HomeLanding
          onPrepareDocument={openSignupModal}
          onLogin={openLoginModal}
        />
      );
    }

    if (appMode === 'smart') {
      return <SmartRedactMode onOpenSettings={openSettings} />;
    }

    // Batch mode views
    if (isBatchMode) {
      if (batchComplete) {
        return (
          <BatchResultDisplay 
            results={batchFiles}
            onReset={handleReset}
            showWatermark={!isPremium}
            redactSession={redactSession.current}
            onOpenSettings={openSettings}
          />
        );
      }
      return (
        <BatchProcessingView 
          files={batchFiles}
          currentIndex={currentBatchIndex}
        />
      );
    }

    // Single file mode views
    switch (appState.status) {
      case Status.IDLE:
        return (
          <InputSelector 
            onFileSelect={handleFileSelect}
            onFilesSelect={handleFilesSelect}
            onTextSubmit={handleTextSubmit}
            maxFiles={maxFiles}
            batchEnabled={batchEnabled}
          />
        );
      case Status.PROCESSING:
        return (
          <ProcessingView 
            fileName={appState.sourceName || undefined} 
            message={appState.statusMessage}
            currentPage={appState.processingPage}
            totalPages={appState.processingTotalPages}
          />
        );
      case Status.SUCCESS:
        return (
          <ResultDisplay 
            redactedText={appState.redactedText!} 
            fileName={appState.sourceName} 
            onReset={handleReset}
            showWatermark={!isPremium}
            onUpgrade={() => setShowUpgradeModal(true)}
            onOpenSettings={openSettings}
            ocrPageFailures={appState.ocrPageFailures}
            pageCount={appState.documentPageCount}
            isPilot={isPilot}
            accountSegment={accountSegment}
            documentCategory={prepareCategoryRef.current}
          />
        );
      case Status.ERROR:
        return (
          <div className="text-center p-8 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
            <h3 className="text-xl font-semibold text-red-700 dark:text-red-400">Processing Failed</h3>
            <p className="mt-2 text-slate-600 dark:text-slate-400">{appState.error}</p>
            <button
              onClick={handleReset}
              className="mt-6 px-6 py-2 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Try Again
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col font-sans">
      <Header
        onUpgradeClick={() => setShowUpgradeModal(true)}
        onTryFreeThenPay={handleTryFreeThenPay}
        proCheckoutLoading={proCheckoutLoading}
        onLoginClick={openLoginModal}
        onSignupClick={openSignupModal}
        onManageSubscriptionClick={() => setShowManageSubscriptionModal(true)}
        onSettingsClick={openSettings}
      />
      <main className="flex-grow container mx-auto px-4 py-8 md:py-12">
        {authNotice && (
          <div className="max-w-4xl mx-auto mb-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 flex items-start justify-between gap-3">
            <p>{authNotice}</p>
            <button
              type="button"
              onClick={clearAuthNotice}
              className="shrink-0 font-medium text-emerald-800 hover:underline"
            >
              Dismiss
            </button>
          </div>
        )}
        {user && (
          <div className="max-w-4xl mx-auto mb-4">
            <div className="inline-flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
              <button
                type="button"
                onClick={() => setAppMode('standard')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                  appMode === 'standard'
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-600 dark:text-slate-300'
                }`}
              >
                Standard Prepare
              </button>
              <button
                type="button"
                onClick={() => setAppMode('smart')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                  appMode === 'smart'
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-600 dark:text-slate-300'
                }`}
              >
                Smart Prepare + Rewrite
              </button>
            </div>
          </div>
        )}
        {isTrialActive && (
          <div className="max-w-4xl mx-auto mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-semibold">
              You have {trialDaysRemaining} day{trialDaysRemaining === 1 ? '' : 's'} remaining
            </p>
            <p className="mt-1">
              Your free trial ends on {trialEndsFormatted}. You will not be charged until {trialEndsFormatted}.
            </p>
          </div>
        )}
        <div className={`mx-auto ${!user ? 'max-w-7xl' : 'max-w-4xl'}`}>
          {renderContent()}
        </div>
        {appMode === 'standard' &&
          appState.status === Status.IDLE &&
          !!user &&
          !isBatchMode && <PositioningSections />}
        {!shouldHidePricingSection && appMode === 'standard' && (
          <div id="pricing" className="scroll-mt-24">
            <PricingTiers onStartFreeTrial={openSignupModal} />
          </div>
        )}
      </main>
      <Footer />

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
      <LimitReachedModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        onUpgrade={handleOpenUpgradeModal}
        limitType={limitModalType}
        details={limitModalDetails}
      />
      <AuthModal
        isOpen={showSignInModal}
        onClose={() => setShowSignInModal(false)}
        onCreateAccountClick={openSignupModal}
      />
      <CreateAccountModal
        isOpen={showCreateAccountModal}
        onClose={() => setShowCreateAccountModal(false)}
        onSignInClick={openLoginModal}
      />
      <ManageSubscriptionModal
        isOpen={showManageSubscriptionModal}
        onClose={() => setShowManageSubscriptionModal(false)}
      />
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />
    </div>
  );
}

function App() {
  return (
    <AppContent />
  );
}

export default App;
